import React from 'react';

import { IconExternalLink } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';

import { Symbol as CfaSymbol } from '@cfa/brand-icons/logos';
import { Button, Modal } from '@cfa/react-core';

interface CertificateLoadingModalProps {
  isOpen: boolean;
  progress: number;
  count: number;
  onCancel: () => void;
}

export const CertificateLoadingModal: React.FC<
  CertificateLoadingModalProps
> = ({ isOpen, progress, count, onCancel }) => {
  const { t } = useTranslation();
  const clampedProgress = Math.min(100, Math.max(0, progress));

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel();
    }
  };

  return (
    <Modal.Overlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable
      className="z-2000!"
    >
      <Modal.Dialog>
        <Modal.Content className="flex! flex-col! items-center! justify-center! px-10! py-16!">
          <CfaSymbol size={64} aria-hidden="true" className="mb-4" />
          <p className="text-grayscale-gray6 mb-6 text-base/6">
            {t('certificates.generating', { count })}
          </p>
          <div className="relative h-5 w-full overflow-hidden rounded-3xl bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxjaXJjbGUgY3g9IjQiIGN5PSI0IiByPSIxLjUiIGZpbGw9IiNkMWQ1ZGIiLz48L3N2Zz4=')] bg-repeat">
            <div
              className="bg-secondary-base flex h-full items-center justify-center rounded-r-3xl transition-all duration-300"
              style={{
                width: `${clampedProgress}%`,
                minWidth: clampedProgress > 0 ? '60px' : '0',
              }}
            >
              {clampedProgress > 0 && (
                <span className="text-sm font-medium text-white">
                  {Math.round(clampedProgress)}%
                </span>
              )}
            </div>
          </div>
        </Modal.Content>
      </Modal.Dialog>
    </Modal.Overlay>
  );
};

export type CertificateViewVariant = 'single' | 'teamMember' | 'plan';

interface CertificateViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewAndSave: () => void;
  variant?: CertificateViewVariant;
}

export const CertificateViewModal: React.FC<CertificateViewModalProps> = ({
  isOpen,
  onClose,
  onViewAndSave,
  variant = 'single',
}) => {
  const { t } = useTranslation();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const isPlural = variant !== 'single';
  const titleKey = isPlural
    ? 'certificates.readyToViewPlural'
    : 'certificates.readyToView';

  const renderDescription = () => {
    switch (variant) {
      case 'teamMember':
        return (
          <>
            <p className="text-grayscale-gray6 mb-3 leading-6 whitespace-nowrap">
              {t('certificates.readyToViewDescriptionTeamMember')}
            </p>
            <p className="text-grayscale-gray6 leading-6">
              <Trans i18nKey="certificates.readyToViewDescriptionTeamMemberNote">
                See <i>Manage Plans</i> or <i>Reports</i> for any plans that
                this Team Member may not have a certificate for.
              </Trans>
            </p>
          </>
        );
      case 'plan':
        return (
          <>
            <p className="text-grayscale-gray6 mb-3 leading-6 whitespace-nowrap">
              {t('certificates.readyToViewDescriptionPlan')}
            </p>
            <p className="text-grayscale-gray6 leading-6">
              <Trans i18nKey="certificates.readyToViewDescriptionPlanNote">
                See <i>Manage Plans</i> or <i>Reports</i> for any Team Members
                that may not have a certificate for this plan.
              </Trans>
            </p>
          </>
        );
      default:
        return (
          <p className="text-grayscale-gray6 leading-6">
            {t('certificates.readyToViewDescription')}
          </p>
        );
    }
  };

  return (
    <Modal.Overlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable
      className="z-2000!"
    >
      <Modal.Dialog className="w-[400px]!">
        <Modal.Title className="text-grayscale-gray6! mt-6! text-center! text-2xl/8! font-bold!">
          {t(titleKey)}
        </Modal.Title>
        <Modal.Content className="px-12! text-center!">
          {renderDescription()}
        </Modal.Content>
        <Modal.Footer className="mt-4! justify-center! pb-12!">
          <Button
            color="secondary"
            onPress={onViewAndSave}
            variant="filled"
            data-testid="view-and-save-button"
          >
            <IconExternalLink className="size-5" />
            {t('certificates.viewAndSave')}
          </Button>
        </Modal.Footer>
      </Modal.Dialog>
    </Modal.Overlay>
  );
};
